/**
 * Entitlement and web checkout on the CMS v1 API (bkfc-cms docs/routes/entitlements.md,
 * checkout.md). Account management (plan change, cancel, card, history, deletion) is not here: it
 * lives only on bkfc.com/account (APP_CONFIG.ACCOUNT_URL). Every call goes through the shared `api`
 * client (Bearer from the session).
 */
import { api, unwrap } from '../api';
import analytics from './analytics';

const UNDETERMINED_CODES = new Set(['ENTITLEMENT_UNDETERMINED', 'UPSTREAM_MISCONFIGURED']);

const dataOf = async request => (await unwrap(request)).data;

/** `amount_minor` in the currency's own minor unit -> a display string. */
export function formatMinor(price) {
  if (!price || price.amount_minor == null || !price.currency) {
    return null;
  }
  try {
    const format = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: price.currency,
    });
    const digits = format.resolvedOptions().maximumFractionDigits;
    return format.format(price.amount_minor / 10 ** digits);
  } catch {
    return null;
  }
}

class SubscriptionService {
  /**
   * `GET /v1/me/entitlement`. 200 is a verdict (`not_entitled` included). 503 / 502 are
   * undetermined: the caller retries after `retryAfterMs` and never shows the paywall. Anything
   * else that fails (network, 5xx) is undetermined too; "we don't know" is never "not subscribed".
   * @returns {Promise<{determined: boolean, subscribed: boolean, entitlement: object|null, retryAfterMs: number|null}>}
   */
  async getEntitlement() {
    try {
      const entitlement = await dataOf(api.GET('/v1/me/entitlement'));
      if (entitlement?.state === 'undetermined') {
        return { determined: false, subscribed: false, entitlement, retryAfterMs: null };
      }
      return {
        determined: true,
        subscribed: entitlement?.state === 'entitled',
        entitlement,
        retryAfterMs: null,
      };
    } catch (error) {
      if (!UNDETERMINED_CODES.has(error?.code)) {
        console.warn('SubscriptionService: entitlement undetermined', error);
      }
      return {
        determined: false,
        subscribed: false,
        entitlement: null,
        retryAfterMs: error?.retryAfterMs ?? null,
      };
    }
  }

  async hasActiveSubscription() {
    const { determined, subscribed } = await this.getEntitlement();
    return determined && subscribed;
  }

  /**
   * `GET /v1/offers`: the card-buyable subscription plans for this viewer's country. An offer the
   * viewer's country cannot buy is absent from the list (never a disabled card).
   */
  async getOffers() {
    const { offers = [] } = (await dataOf(api.GET('/v1/offers'))) || {};
    return offers
      .filter(o => o.offer_kind === 'subscription' && o.checkout?.cleeng_offer_id)
      .map(o => ({
        _id: o.id,
        name: o.name,
        cleeng_offer_id: o.checkout.cleeng_offer_id,
        price_display: o.price_display ?? formatMinor(o.price),
        // Billing cycle and card-checkout trial, mirrored from Cleeng; null when it sent none.
        period: o.period ?? null,
        trial_days: o.trial_days ?? null,
      }));
  }

  /** `POST /v1/checkout/orders` -> `{ order, paymentGateway }` (the drop-in configuration). */
  async createOrder(product) {
    const { order, payment_gateway: paymentGateway } = await dataOf(
      api.POST('/v1/checkout/orders', { body: { cleeng_offer_id: product.cleeng_offer_id } }),
    );
    // Remember the order's price so `purchase` reports revenue even though finalize carries none.
    this.pendingOrders = this.pendingOrders || new Map();
    this.pendingOrders.set(String(order.id), {
      value: order.total_price,
      currency: order.currency,
      productId: product._id,
      planName: product.name,
    });
    analytics.trackBeginCheckout({ item_id: product._id, currency: order.currency });
    return { order: { ...order, amount: order.total_price }, paymentGateway };
  }

  /** `POST /v1/checkout/payments` -> `{ status, action, success }` verbatim. */
  async submitPayment({
    orderId,
    cleengOfferId,
    paymentMethodData,
    returnUrl,
    origin,
    browserInfo,
    billingAddress,
  }) {
    const result = await dataOf(
      api.POST('/v1/checkout/payments', {
        body: {
          order_id: orderId,
          cleeng_offer_id: cleengOfferId,
          payment_method: paymentMethodData,
          return_url: returnUrl,
          origin,
          ...(browserInfo ? { browser_info: browserInfo } : {}),
          ...(billingAddress ? { billing_address: billingAddress } : {}),
        },
      }),
    );
    if (result?.success) {
      this.trackPurchase(orderId);
    }
    return result;
  }

  /** `POST /v1/checkout/payments/finalize`: the 3DS `onAdditionalDetails` or `redirectResult`. */
  async finalizePayment({ orderId, cleengOfferId, details }) {
    const result = await dataOf(
      api.POST('/v1/checkout/payments/finalize', {
        body: { order_id: orderId, cleeng_offer_id: cleengOfferId, details },
      }),
    );
    if (result?.success) {
      this.trackPurchase(orderId);
    }
    return result;
  }

  trackPurchase(orderId) {
    const remembered = this.pendingOrders?.get(String(orderId)) || {};
    analytics.trackPurchase({ transactionId: orderId, ...remembered });
    this.pendingOrders?.delete(String(orderId));
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
